<template>
  <v-layout row wrap>
    <v-flex
      md10
      offset-md1
      lg8
      offset-lg2>
        <v-layout v-if="apiLoading" row wrap>
          <v-flex xs12>
            <ConfirmationLoader />
          </v-flex>
        </v-layout>

      <template v-if="!apiLoading">
        <div v-if="hasApiError" class="confirmation-summary">
          <v-layout row wrap class="thankyou-container">
            <v-flex xs12>
              <BaseAlert
                v-if="apiError"
                :hg-value="true"
                :hg-lead-in="apiError.body.message"
                hg-type="error"
                ref="api-error-alert">
                <ul v-for="violation in apiError.body.fieldViolations" :key="violation.field">
                  <li
                    v-for="(error, index) in violation.messages"
                    :key="`${violation.field}_${index}`">
                    {{error}}
                  </li>
                </ul>
              </BaseAlert>

              <BaseAlert
                v-if="apiErrorAgents
                  || apiErrorClients
                  || apiErrorInspectors
                  || apiErrorInvoice"
                :hg-value="true"
                hg-type="error"
                ref="api-ancillary-error-alert">
                <ul>
                  <li v-if="apiErrorAgents">
                    Unable to retrieve Agents for this Appointment
                  </li>
                  <li v-if="apiErrorClients">
                    Unable to retrieve Clients for this Appointment
                  </li>
                  <li v-if="apiErrorInspectors">
                    Unable to retrieve Inspectors for this Appointment
                  </li>
                  <li v-if="apiErrorInvoice">
                    Unable to retrieve Services Estimates for this Appointment
                  </li>
                </ul>
              </BaseAlert>
            </v-flex>
          </v-layout>
        </div>

        <div v-if="model" class="confirmation-summary">
          <ConfirmationSummaryIntro :company-url="company.websiteUrl" :status="model.status" />

          <div v-if="model" class="confirmation-summary--sections">
            <v-layout hidden-print-only row wrap>
              <v-flex xs12 class="print-button text-xs-center text-md-right">
                <a @click="print">
                  <v-icon>print</v-icon>
                  Print this page
                </a>
              </v-flex>
            </v-layout>

            <div class="confirmation-summary--section">
              <ConfirmationSummaryAppointment
                :appointment="model.start"
                :company-name="company.name"
                :inspectors="inspectors" />
            </div>
            <div class="confirmation-summary--section">
              <ConfirmationSummaryProperty :model="model" />
            </div>
            <div v-if="propertyDetailsIsEnabled" class="confirmation-summary--section">
              <ConfirmationSummaryPropertyDetails :model="model" />
            </div>

            <div v-if="hasInvoices" class="confirmation-summary--section">
              <ConfirmationSummaryInvoices :invoices="invoices" />
            </div>

            <div v-if="agents || clients" class="confirmation-summary--section">
              <ConfirmationSummaryContacts :agents="agents" :clients="clients" />
            </div>
          </div>

          <v-layout row wrap class="footer">
            <v-flex sm12>
              <SummaryTitle>Need to cancel or reschedule?</SummaryTitle>
            </v-flex>

            <v-flex sm12>
              <p class="please">
                Please contact us in the event that you need
                to cancel or reschedule your appointment.
              </p>
            </v-flex>

            <template v-if="company.phone">
              <v-flex shrink hidden-md-and-up>
                <span class="phone">
                  <a :href="`tel:${company.phone}`">{{company.phone}}</a>
                </span>
              </v-flex>

              <v-flex shrink hidden-sm-and-down>
                <span class="phone">{{company.phone}}</span>
              </v-flex>
            </template>

            <v-flex shrink hidden-sm-and-down>
              <div class="pipe" />
            </v-flex>

            <v-flex shrink v-if="company.email">
              <a :href="`mailto:${company.email}`" class="email">{{company.email}}</a>
            </v-flex>
          </v-layout>
        </div>

        <v-layout v-else row wrap>
          <v-flex xs12>
            <BaseAlert
              :hg-value="true"
              hg-lead-in="Confirmation not Found"
              hg-type="warning">
              <p>
                We couldn't find that Confirmation.
                Check that your URL is correct and try again.
              </p>
            </BaseAlert>
          </v-flex>
        </v-layout>
      </template>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { Components } from '@homegauge/vue';

import ConfirmationSummaryAppointment from '@/components/ConfirmationSummaryAppointment.vue';
import ConfirmationSummaryContacts from '@/components/ConfirmationSummaryContacts.vue';
import ConfirmationSummaryIntro from '@/components/ConfirmationSummaryIntro.vue';
import ConfirmationSummaryInvoices from '@/components/ConfirmationSummaryInvoices.vue';
import ConfirmationSummaryProperty from '@/components/ConfirmationSummaryProperty.vue';
import ConfirmationSummaryPropertyDetails from '@/components/ConfirmationSummaryPropertyDetails.vue';
import ConfirmationLoader from '@/components/ConfirmationLoader.vue';
import SummaryTitle from '@/components/common/SummaryTitle.vue';

const { BaseAlert } = Components;

export default {
  components: {
    BaseAlert,
    ConfirmationSummaryAppointment,
    ConfirmationSummaryContacts,
    ConfirmationSummaryIntro,
    ConfirmationSummaryInvoices,
    ConfirmationSummaryProperty,
    ConfirmationSummaryPropertyDetails,
    ConfirmationLoader,
    SummaryTitle,
  },
  data() {
    return {
      apiError: null,
      apiErrorAgents: false,
      apiErrorClients: false,
      apiErrorInspectors: false,
      apiErrorInvoice: false,
      apiLoading: false,
      agents: null,
      clients: null,
      inspectors: null,
      invoices: [],
    };
  },
  mounted() {
    this.initialHydration();
  },
  computed: {
    ...mapGetters('Company', ['propertyDetailsIsEnabled']),
    ...mapState('Appointment', {
      model: state => state.bookingData,
    }),
    ...mapState('Company', {
      company: state => state.company,
    }),
    hasApiError() {
      return this.apiError
        || this.apiErrorAgents
        || this.apiErrorClients
        || this.apiErrorInspectors
        || this.apiErrorInvoice;
    },
    hasInvoices() {
      return !!(this.invoices && this.invoices.length > 0);
    },
  },
  methods: {
    async initialHydration() {
      this.apiLoading = true;
      await this.retrieveAppointmentData();
      await this.retrieveInspectors();
      await this.retrieveClients();
      await this.retrieveAgents();
      await this.retrieveInvoiceData();
      this.apiLoading = false;
    },
    print() {
      window.print();
    },
    async retrieveAgents() {
      const reps = this.model.buyerReps.length > 0 ? this.model.buyerReps : this.model.sellerReps;
      if (reps.length < 1) {
        return;
      }

      try {
        const requests = reps.map(repId => this.$store.dispatch('Person/loadRep', repId));
        this.agents = await Promise.all(requests);
      } catch (err) {
        this.agents = null;
        this.apiErrorAgents = true;
      }
    },
    async retrieveClients() {
      const clients = this.model.buyers.length > 0 ? this.model.buyers : this.model.sellers;
      if (clients.length < 1) {
        return;
      }

      try {
        const requests = clients.map(clientId => this.$store.dispatch('Person/loadCustomer', clientId));
        this.clients = await Promise.all(requests);
      } catch (err) {
        this.clients = null;
        this.apiErrorClients = true;
      }
    },
    async retrieveInspectors() {
      const { inspectors } = this.model;
      if (inspectors.length < 1) {
        return;
      }

      try {
        const requests = inspectors.map(inspectorId => this.$store.dispatch('Person/loadInspector', inspectorId));
        this.inspectors = await Promise.all(requests);
      } catch (err) {
        this.inspectors = null;
        this.apiErrorInspectors = true;
      }
    },
    async retrieveAppointmentData() {
      const currentAppointmentDataLoaded = this.model
        && (this.$route.params.appointmentId === this.model.id);

      if (currentAppointmentDataLoaded) {
        return;
      }

      try {
        await this.$store.dispatch(
          'Appointment/loadAppointment',
          this.$route.params.appointmentId,
        );
      } catch (err) {
        this.apiError = err;
      }
    },
    async retrieveInvoiceData() {
      const { appointmentId } = this.$route.params;
      try {
        this.invoices = await this.$store.dispatch(
          'Appointment/loadAppointmentInvoices',
          appointmentId,
        );
      } catch (err) {
        this.invoices = [];
        this.apiErrorInvoice = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/mixins/breakpoints';
@import '../styles/mixins/layout';
@import '../styles/vars/theme';
@import '../styles/vars/typography';

.print-button {
  font-weight: $typography-weight-bold;

  i {
    color: $theme-color-secondary;
    font-size: 1.8rem;
    margin: 0 0.4rem 0 0;
    vertical-align: middle;
  }
}


.please {
  color: $theme-color-gray;
  font-size: 1.2em;
  font-weight: 500;
  letter-spacing: -0.0125rem;
  margin: 10px 0;
}

.phone,
.email {
  font-size: 1.2rem;
  font-weight: 700;
}

.pipe {
  background: $theme-color-gray-lightest;
  display: inline-block;
  height: 18px;
  margin: 4px 8px;
  width: 2px;
}

.contact {
  padding-top: 15px;
}

.confirmation-summary {
  &--sections {
    border-top: 4px solid $theme-color-primary;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    padding: 1rem 2.8rem;
    margin: 35px 0;
  }

  &--section {
    border-bottom: 1px solid $theme-color-gray-lightest;
    padding: 10px 0;

    &:first-child {
      padding: 0 0 10px;
    }

    &:last-child {
      border: none;
      padding: 10px 0 0;
    }
  }
}

@include responsive-breakpoint(sm) {
  .thankyou-container {
    text-align: center;

    h3 {
      font-weight: 700;
    }
  }

  .confirmation-summary {
    &--sections {
      border-top: none;
      box-shadow: none;
      margin: 15px 0;
      padding: 0;
    }
  }

  .please {
    font-size: 1.15rem;
  }
}

@include responsive-breakpoint(print) {
  .phone-link,
  .thankyou-container {
    display: none;
  }

  .confirmation-summary--sections,
  .confirmation-summary--section,
  .confirmation-summary-row {
    @include clear-box-spacing;
    box-shadow: none;
  }
}
</style>
